// Port of ios/Sources/ViewModels/ThreadFooterDerivation.swift.
//
// The "N respuestas" footer under a root post in the CHANNEL — the desktop's reply bar, and the
// way into a thread from the conversation it started in. A decision-for-decision port of
// Android's `ThreadFooterDerivation`, tests included. Pure: nothing here reads the store.
import type { PostRecord, ThreadRecord } from './records';

/**
 * One thread's cached shape, straight from the store: reply count, newest reply time, and the
 * distinct repliers in first-reply order (root author NOT included — the DAO does not know it;
 * the caller holding the root post does).
 */
export interface ThreadSummary {
  rootId: string;
  replyCount: number;
  lastReplyAt: number;
  participantIds: string[];
}

export interface ThreadFooter {
  /** The thread to open when the footer is tapped — always the ROOT. */
  rootId: string;
  replyCount: number;
  /** The facepile, ROOT AUTHOR FIRST then repliers in reply order, capped and deduped. */
  avatarUserIds: string[];
  /** How many more people are in the thread than the facepile shows; 0 when it shows all. */
  overflowCount: number;
  lastReplyAt: number;
  /**
   * How many of those replies the reader has not read, from the server's own per-thread
   * counters (the `threads` table), NOT from the local post cache. 0 both when the thread is
   * fully read and when the reader does not follow it — Mattermost only tracks unread for
   * followed threads, and an unfollowed thread has no unread to report.
   */
  unreadReplies: number;
  /** How many of the unread replies name the reader. Always ≤ `unreadReplies`. */
  unreadMentions: number;
}

/** Four faces is what fits beside a count at mobile width without the row growing. */
const facepileCap = 4;

/** Whether this footer is announcing something new rather than just summarising. */
function isUnread(footer: ThreadFooter): boolean {
  return footer.unreadReplies > 0 || footer.unreadMentions > 0;
}

/**
 * Footers for a whole page of posts, keyed by the post the footer goes under. Only ROOT
 * posts get one: a reply rendered inline must NOT carry a footer of its own — it would
 * repeat the same thread's count under every one of its own replies. A root with zero known
 * replies is absent rather than mapped to a zero footer: "no replies yet" is what an
 * ordinary message already looks like, and a bar claiming nothing is clutter.
 *
 * **`threads` can put a footer where `summaries` cannot, and that is the point.** The
 * summaries are counted from the LOCAL post cache, and replies land there only when a thread
 * is opened — so a thread the reader has never opened, which is exactly the one with unread
 * replies in it, had no footer at all. The conversation then showed nothing whatsoever to
 * distinguish the root post carrying three unread answers from the one carrying none
 * ("easier to see which thread in the conversation hasn't been read"). The server's own row
 * for a followed thread knows its reply count without any of its replies being cached, so it
 * is both the unread source and the fallback count.
 */
function footers(
  posts: PostRecord[],
  summaries: ReadonlyMap<string, ThreadSummary>,
  threads: ReadonlyMap<string, ThreadRecord> = new Map(),
): Map<string, ThreadFooter> {
  const result = new Map<string, ThreadFooter>();
  if (summaries.size === 0 && threads.size === 0) return result;
  for (const post of posts) {
    if (post.rootId) continue;
    const summary = summaries.get(post.id);
    const thread = threads.get(post.id);
    // The higher of the two counts wins, and neither is "more correct" in general: the
    // local one can be ahead (a reply just arrived over the socket, before any threads
    // refresh) and the server one can be ahead (replies never cached on this device).
    // Taking the max is the only choice that never under-reports a thread that exists.
    const replyCount = Math.max(summary?.replyCount ?? 0, thread?.replyCount ?? 0);
    if (replyCount <= 0) continue;
    result.set(post.id, footer(post, summary, thread, replyCount));
  }
  return result;
}

/**
 * The root's author leads the facepile even when they never replied — they started the
 * thread, and a facepile that omits them reads as a conversation the reader has to open to
 * identify. Deduped so an author who also replied appears once.
 */
function footer(
  root: PostRecord,
  summary: ThreadSummary | undefined,
  thread: ThreadRecord | undefined,
  replyCount: number,
): ThreadFooter {
  const everyone: string[] = [];
  if (root.userId !== '') everyone.push(root.userId);
  for (const id of summary?.participantIds ?? []) {
    if (id !== '' && !everyone.includes(id)) everyone.push(id);
  }
  return {
    rootId: root.id,
    replyCount,
    avatarUserIds: everyone.slice(0, facepileCap),
    overflowCount: Math.max(0, everyone.length - facepileCap),
    // Same reasoning as the count: whichever source knows about a newer reply is right.
    lastReplyAt: Math.max(summary?.lastReplyAt ?? 0, thread?.lastReplyAt ?? 0),
    unreadReplies: thread?.unreadReplies ?? 0,
    unreadMentions: thread?.unreadMentions ?? 0,
  };
}

/**
 * The replies cached on this device and the server's own count for each root
 * (`collapsedThreads=true` pages, `root_reply_stats`), as ONE summary per root: the higher count
 * and the later reply win (each side can be ahead), and the faces are the local repliers first,
 * then the server's, eight at most. Twin of Swift `ThreadFooterDerivation.mergedSummaries`.
 */
function mergedSummaries(
  local: ReadonlyMap<string, ThreadSummary>,
  server: ReadonlyMap<string, { replyCount: number; lastReplyAt: number; participantIds: readonly string[] }>,
): Map<string, ThreadSummary> {
  const result = new Map(local);
  for (const [rootId, stats] of server) {
    const mine = local.get(rootId);
    const faces = [...(mine?.participantIds ?? [])];
    for (const id of stats.participantIds) if (faces.length < 8 && !faces.includes(id)) faces.push(id);
    const replyCount = Math.max(mine?.replyCount ?? 0, stats.replyCount);
    if (replyCount === 0) continue; // a root with no replies has no footer, as before
    result.set(rootId, { rootId, replyCount, lastReplyAt: Math.max(mine?.lastReplyAt ?? 0, stats.lastReplyAt), participantIds: faces });
  }
  return result;
}

export const ThreadFooterDerivation = {
  facepileCap,
  isUnread,
  footers,
  mergedSummaries,
} as const;
